import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";

/**
 * Privacy, Terms, Security and GDPR.
 *
 * The content here is a factual description of what the software actually
 * does -- what it stores, how it encrypts, what it deletes -- because that is
 * something the build can be checked against. It is deliberately not written
 * as a binding legal document: a policy that reads as settled but has never
 * been reviewed is worse than an honest placeholder, because people rely on
 * it. Each page says so at the top.
 */

const CONTACT = process.env.REACT_APP_CONTACT_EMAIL || "hello@example.com";

const PAGES = {
  privacy: {
    title: "Privacy",
    intro:
      "What SocialMind stores, why it stores it, and how long it keeps it.",
    sections: [
      {
        h: "What we store",
        p: [
          "Account details: your name, email address, and a bcrypt hash of your password. We never store the password itself.",
          "Workspace content: the posts you draft, the media you upload, comments left during review, and the record of who approved what.",
          "Connected accounts: an encrypted access token for each social account or drive you connect. Tokens are encrypted at rest and are excluded from every data export.",
          "Activity: an audit log of significant actions, including any time platform support opens your workspace.",
        ],
      },
      {
        h: "What we do not store",
        p: [
          "Card details. Payments are handled by our payment provider; we hold a subscription reference, not a card number.",
          "The contents of your connected drives. We store only what you import.",
        ],
      },
      {
        h: "Getting your data out, or deleting it",
        p: [
          "A workspace admin can export everything held for that workspace as JSON, and anyone can export their own record.",
          "Deleting your account redacts your identity and keeps the shape of your work, so a colleague's approval history does not develop holes. Deleting a workspace removes its data entirely.",
        ],
      },
      {
        h: "Support access",
        p: [
          "Platform support can open a workspace read-only, for thirty minutes, and only with a written reason. Every grant is written to that workspace's own audit log, so you can see it.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms",
    intro: "The agreement between you and SocialMind.",
    sections: [
      {
        h: "Not yet drafted",
        p: [
          "Terms of service are a contract, and drafting one is a job for a lawyer who understands where you operate and what you are selling.",
          "This page exists so the link in the footer goes somewhere honest rather than nowhere. It must carry real terms before you take a paying customer.",
        ],
      },
    ],
  },
  security: {
    title: "Security",
    intro: "How the product is built to keep one customer's data away from another's.",
    sections: [
      {
        h: "Separation between workspaces",
        p: [
          "Every record belongs to exactly one workspace, and every query is scoped to it in the application layer.",
          "The PostgreSQL schema adds row-level security underneath that, so a query that forgets its filter returns nothing rather than another customer's data. The database role the application connects as cannot bypass those policies.",
        ],
      },
      {
        h: "Credentials",
        p: [
          "Passwords are hashed with bcrypt. Access tokens for connected accounts, and two-factor secrets, are encrypted at rest.",
          "Password reset and email verification links are stored only as hashes, work once, and expire.",
          "Platform administrators must enrol a second factor before the admin console will open.",
        ],
      },
      {
        h: "Abuse protection",
        p: [
          "Sign-in is rate limited per account and per address, so one is not a way around the other.",
          "Payment webhooks are verified by signature against the raw request body, and each event is applied once.",
        ],
      },
      {
        h: "Reporting something",
        p: [
          `If you find a security problem, write to ${CONTACT}. Please give us a reasonable window to fix it before publishing.`,
        ],
      },
    ],
  },
  gdpr: {
    title: "GDPR",
    intro: "Data protection rights, and how to exercise them here.",
    sections: [
      {
        h: "Access and portability",
        p: [
          "Settings has an export for your own record, and a workspace-wide export for admins. Both return JSON rather than a PDF, so the data is actually usable.",
        ],
      },
      {
        h: "Erasure",
        p: [
          "You can delete your account from Settings. Your details are removed and your work stays attributed to a deleted user, so colleagues' records remain intact.",
          "Audit entries recording the deletion are kept. Erasing the proof that an erasure happened would defeat the purpose.",
        ],
      },
      {
        h: "Where data is held",
        p: [
          "Workspaces on the Enterprise plan can be pinned to a region. A deployment refuses to serve a workspace held in a different region rather than quietly serving it from the wrong place.",
        ],
      },
      {
        h: "Still outstanding",
        p: [
          "A data processing agreement and a list of sub-processors both need drafting before you sell into the EU. Neither is written yet.",
        ],
      },
    ],
  },
};

export default function Legal() {
  const { pathname } = useLocation();
  const key = pathname.replace("/", "") || "privacy";
  const page = PAGES[key] || PAGES.privacy;

  useEffect(() => {
    document.title = `${page.title} — SocialMind`;
    window.scrollTo(0, 0);
  }, [page.title]);

  return (
    <div className="bg-white">
      <MarketingHeader />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="font-display text-4xl font-bold tracking-tight text-[#0B2B33]">{page.title}</h1>
        <p className="mt-3 text-lg text-[#43535E]">{page.intro}</p>

        <div className="mt-8 flex gap-3 rounded-xl border border-[#E4E9EF] bg-[#FFF4E2] p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#B26A00]" />
          <p className="text-sm text-[#43535E]">
            This describes how the software behaves today. It has not been reviewed by a lawyer
            and is not a binding policy yet. Have it reviewed before you take a paying customer.
          </p>
        </div>

        <div className="mt-10 space-y-9">
          {page.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-display text-xl font-semibold text-[#0B2B33]">{s.h}</h2>
              <div className="mt-2 space-y-3">
                {s.p.map((para) => (
                  <p key={para} className="text-[#43535E]">{para}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-4 border-t border-[#E4E9EF] pt-6 text-sm">
          {Object.entries(PAGES)
            .filter(([k]) => k !== key)
            .map(([k, v]) => (
              <Link key={k} to={`/${k}`} className="font-semibold text-[#1F5FD8] hover:underline">
                {v.title}
              </Link>
            ))}
          <a href={`mailto:${CONTACT}`} className="font-semibold text-[#1F5FD8] hover:underline">
            Contact us
          </a>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
